import React, { createContext, useContext, useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";

export type User = {
  id: string;
  full_name: string;
  email: string;
  timezone: string;
  default_organization_id: string | null;
  is_platform_admin: boolean;
  quiet_hours_start: string;
  quiet_hours_end: string;
  proactive_message_cap: number;
  always_review_on_web: boolean;
};

type AuthState = {
  user: User | null | undefined; // undefined = loading
  refreshMe: () => Promise<void>;
  login: (email: string, password: string) => Promise<User>;
  signup: (full_name: string, email: string, password: string) => Promise<User>;
  logout: () => Promise<void>;
  setUser: (u: User) => void;
};

const AuthContext = createContext<AuthState>({} as AuthState);
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null | undefined>(undefined);

  const refreshMe = useCallback(async () => {
    try {
      const r = await api.get("/auth/me");
      setUser(r.data);
    } catch {
      setUser(null);
    }
  }, []);

  useEffect(() => {
    refreshMe();
  }, [refreshMe]);

  const login = useCallback(async (email: string, password: string) => {
    const r = await api.post("/auth/login", { email, password });
    setUser(r.data);
    return r.data;
  }, []);

  const signup = useCallback(async (full_name: string, email: string, password: string) => {
    const r = await api.post("/auth/register", { full_name, email, password });
    setUser(r.data);
    return r.data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } finally {
      setUser(null);
    }
  }, []);

  const value = useMemo(
    () => ({ user, refreshMe, login, signup, logout, setUser }),
    [user, refreshMe, login, signup, logout],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
